import { Search, X } from "lucide-react"
import { forwardRef, useRef, type ComponentPropsWithoutRef } from "react"
import { cn } from "../../lib/utils"
import { Input } from "./input"

/** Visible size the search field renders at. */
export type SearchFieldSize = "sm" | "md"

/**
 * The field's whole state progression: a white box behind a hairline, and one
 * border-colour step when the pointer is over it or the caret is in it.
 * Nothing else moves — no fill change, no ring, no halo. A search field sits
 * in a toolbar beside quiet controls, and anything louder makes it the subject
 * of the row rather than a way to narrow what is below.
 */
const FIELD_STATE_CLASS = [
  "border-[rgba(24,24,24,0.12)] bg-white transition-colors duration-v2-quick",
  "hover:border-[rgba(24,24,24,0.2)]",
  "data-[state=focused]:border-[rgba(24,24,24,0.2)] data-[state=focused]:bg-white data-[state=focused]:ring-0 data-[state=focused]:shadow-none",
].join(" ")

/** Ink and box for the magnifier, which the field's own icon slot would otherwise force to 16px. */
const ICON_CLASS: Record<SearchFieldSize, string> = {
  sm: "left-2 text-[rgba(24,24,24,0.56)] [&_svg]:size-3",
  md: "left-2.5 text-[rgba(24,24,24,0.56)] [&_svg]:size-3.5",
}

/**
 * The clear control's box and glyph. The field's own right slot sizes any
 * button it holds to 36px and any glyph to 16px — fine for a 36px field with
 * an icon button in it, far too heavy for a 30px search rail — so both are set
 * back down here.
 */
const RIGHT_SLOT_CLASS: Record<SearchFieldSize, string> = {
  sm: "pr-2 [&_button]:size-[18px] [&_svg]:size-[9px]",
  md: "pr-2.5 [&_button]:size-5 [&_svg]:size-[10px]",
}

/** Room for the magnifier on the left and the clear control on the right. */
const INPUT_PAD_CLASS: Record<SearchFieldSize, string> = {
  sm: "pl-[26px] pr-7",
  md: "pl-8 pr-8",
}

export interface SearchFieldProps
  extends Omit<
    ComponentPropsWithoutRef<typeof Input>,
    "type" | "leftIcon" | "rightIcon" | "value" | "onChange" | "label" | "aria-label" | "classNames" | "size"
  > {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** Used as the field's `aria-label`, since a visible label reads as noise on a search rail. */
  label: string
  /** Step on the v2 control ladder. `sm` reproduces the compact rail geometry; `md` (default) leaves the Input's own geometry alone. */
  size?: SearchFieldSize
  className?: string
}

/**
 * Controlled search input for narrowing a list. Wraps the v2 `Input` with a
 * search icon, a clear button that only shows once there is a query, and no
 * debouncing of its own — callers debounce upstream.
 */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField({ value, onChange, placeholder, label, size = "md", className, ...rest }, ref) {
    const inputRef = useRef<HTMLInputElement>(null)

    return (
      <Input
        {...rest}
        ref={(node) => {
          inputRef.current = node
          if (typeof ref === "function") ref(node)
          else if (ref) ref.current = node
        }}
        type="search"
        value={value}
        onChange={onChange}
        aria-label={label}
        placeholder={placeholder}
        className={className}
        leftIcon={<Search size={size === "sm" ? 12 : 14} />}
        classNames={{
          field: cn(FIELD_STATE_CLASS, size === "sm" && "h-[30px]"),
          leftIcon: ICON_CLASS[size],
          rightIcon: RIGHT_SLOT_CLASS[size],
          input: cn(
            INPUT_PAD_CLASS[size],
            size === "sm" && "text-[13px] leading-[18px]",
            "text-[#181818]",
          ),
        }}
        rightIcon={
          value ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                onChange("")
                inputRef.current?.focus()
              }}
              /* A small glyph on its own round wash, with the hit area pushed
                 back out past it so the target stays comfortable. */
              /* Sized by the slot above; the hit area is pushed back out past
                 the wash so the target stays comfortable at this scale. */
              className="relative rounded-full text-[rgba(24,24,24,0.45)] transition-colors before:absolute before:-inset-1.5 before:content-[''] hover:bg-black/[0.06] hover:text-[#181818] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring"
            >
              <X strokeWidth={2.25} />
            </button>
          ) : undefined
        }
      />
    )
  },
)
